import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../auth/auth.context'
import { FEATURES } from '../../config/features.config'
import { useChatUnread } from '../../hooks/use-chat-unread'
import {
  Book,
  Clock,
  CalendarDays,
  Home,
  Lock,
  User,
  Users,
  MessageSquare,
  MessageCircle,
  GraduationCap,
  CreditCard,
  LogOut,
  Menu,
  X,
} from 'lucide-react'

interface NavItem {
  label: string
  path?: string
  icon: React.ReactNode
  roles?: readonly ('ADMIN' | 'SUPERADMIN' | 'EMPLOYEE')[]
  action?: () => void
  locked?: boolean
}

export default function NavigationDrawer() {
  const { t } = useTranslation()
  const { user, company, logout } = useAuth()
  const location = useLocation()
  const [isOpen, setIsOpen] = useState(false)

  const companyLogoUrl = (company as any)?.logoUrl
  const isApproved = (company as any)?.isApproved ?? true
  const { data: chatUnread = 0 } = useChatUnread()

  const navItems: NavItem[] = [
    { label: t('nav.dashboard'), path: '/', icon: <Home className="w-5 h-5" /> },
    { label: t('nav.modules'), path: '/modules', icon: <Book className="w-5 h-5" /> },
    { label: t('nav.weiterbildung'), path: '/weiterbildung', icon: <GraduationCap className="w-5 h-5" /> },
    { label: t('nav.employees'), path: '/users', icon: <Users className="w-5 h-5" />, roles: ['ADMIN', 'SUPERADMIN'] },
    { label: t('nav.bulletinBoard'), path: '/bulletin-board', icon: <MessageSquare className="w-5 h-5" />, roles: ['ADMIN'] },
    ...(FEATURES.timeTracking
      ? [
          {
            label: t('nav.timeTracking'),
            path: '/time-tracking',
            icon: <Clock className="w-5 h-5" />,
            roles: ['ADMIN'] as const,
            locked: !isApproved,
          },
        ]
      : []),
    {
      label: t('nav.chat'),
      path: '/chat',
      icon: <MessageCircle className="w-5 h-5" />,
      roles: ['ADMIN'] as const,
    },
    ...(FEATURES.absenceRequests
      ? [
          {
            label: t('nav.absenceRequests'),
            path: '/absence-requests',
            icon: <CalendarDays className="w-5 h-5" />,
            roles: ['ADMIN'] as const,
            locked: !isApproved,
          },
        ]
      : []),
    {
      label: t('nav.subscription'),
      path: '/subscription',
      icon: <CreditCard className="w-5 h-5" />,
      roles: ['ADMIN', 'SUPERADMIN'],
    },
    { label: t('nav.profile'), path: '/profile', icon: <User className="w-5 h-5" /> },
    {
      label: t('nav.companies'),
      path: '/companies',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      roles: ['SUPERADMIN'],
    },
    {
      label: t('nav.billing'),
      path: '/billing-overview',
      icon: <CreditCard className="w-5 h-5" />,
      roles: ['SUPERADMIN'],
    },
  ]

  const filteredNavItems = navItems.filter(
    item => !item.roles || (user?.role && item.roles.includes(user.role))
  )

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-3 left-3 z-50 p-2 rounded-lg bg-neutral-900 text-white lg:hidden shadow-md"
        aria-label={t('common.menu')}
      >
        {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-[248px] z-40 flex flex-col transform transition-transform duration-200 ease-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}
        style={{ backgroundColor: '#1B1D3C' }}
      >
        {/* Brand header */}
        <div className="flex items-center gap-3 px-5 h-[56px] border-b" style={{ borderColor: '#2A2D52' }}>
          {companyLogoUrl ? (
            <img
              src={companyLogoUrl}
              alt={company?.name || t('common.logo')}
              className="w-8 h-8 rounded-md object-contain bg-white/10"
            />
          ) : (
            <div
              className="w-8 h-8 rounded-md flex items-center justify-center text-white text-sm font-bold"
              style={{ backgroundColor: '#354ECE' }}
            >
              {company?.name?.charAt(0) || 'A'}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white truncate">{company?.name || t('nav.dashboard')}</p>
            <p className="text-xs truncate" style={{ color: '#A8ABD0' }}>
              {user?.firstName} {user?.lastName}
            </p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-3 px-3">
          <div className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-wider" style={{ color: '#6B6E94' }}>
            {t('nav.navigation')}
          </div>
          <div className="space-y-0.5">
            {filteredNavItems.map(item => {
              const active = item.path ? isActive(item.path) : false

              if (item.locked) {
                return (
                  <div
                    key={item.path || item.label}
                    className="flex items-center gap-3 px-3 py-2 rounded-md text-sm opacity-40 cursor-not-allowed"
                    title={t('nav.lockedFeatureHint')}
                  >
                    <span style={{ color: '#6B6E94' }}>{item.icon}</span>
                    <span className="flex-1" style={{ color: '#6B6E94' }}>{item.label}</span>
                    <Lock className="w-3.5 h-3.5" style={{ color: '#6B6E94' }} />
                  </div>
                )
              }

              if (item.path) {
                const showChatDot = item.path === '/chat' && chatUnread > 0
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className="relative flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-100"
                    style={{
                      backgroundColor: active ? '#2F3360' : 'transparent',
                      color: active ? '#FFFFFF' : '#A8ABD0',
                    }}
                    onMouseEnter={e => {
                      if (!active) {
                        e.currentTarget.style.backgroundColor = '#252849'
                        e.currentTarget.style.color = '#FFFFFF'
                      }
                    }}
                    onMouseLeave={e => {
                      if (!active) {
                        e.currentTarget.style.backgroundColor = 'transparent'
                        e.currentTarget.style.color = '#A8ABD0'
                      }
                    }}
                  >
                    {active && (
                      <span
                        className="absolute left-0 w-[3px] h-6 rounded-r-full"
                        style={{ backgroundColor: '#5B6AE0' }}
                      />
                    )}
                    <span>{item.icon}</span>
                    <span className="flex-1">{item.label}</span>
                    {showChatDot && (
                      <span className="w-2 h-2 rounded-full bg-error-500 flex-shrink-0" />
                    )}
                  </Link>
                )
              }

              return null
            })}
          </div>
        </nav>

        {/* Logout */}
        <div className="px-3 pb-4 border-t" style={{ borderColor: '#2A2D52' }}>
          <button
            onClick={() => {
              logout()
              setIsOpen(false)
            }}
            className="flex items-center gap-3 w-full px-3 py-2.5 mt-3 rounded-md text-sm font-medium transition-colors duration-100"
            style={{ color: '#A8ABD0' }}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#252849'
              e.currentTarget.style.color = '#FFFFFF'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.color = '#A8ABD0'
            }}
          >
            <LogOut className="w-5 h-5" />
            <span>{t('nav.logout')}</span>
          </button>
        </div>
      </aside>
    </>
  )
}
